import type { CirclePackNode, SearchMatch } from '../shared/types.js';
import { chartInstance, highlightedCircles, highlightedNodeNames, exactMatchNodeNames, setSearchTerm, currentSearchTerm, showContentNodesActive } from './state.js';
import { displayNodeData } from './panel.js';
import { highlightContentNodes } from './contentFilter.js';
import { showErrorModal } from './utils/modalUtil.js';
import { t } from './utils/i18n.js';

export function searchAndHighlight(searchTerm: string): void {
    if (!searchTerm.trim() || !chartInstance) return;

    highlightedCircles.forEach(item => {
        item.circle.style.stroke = item.originalStroke;
        item.circle.style.strokeWidth = item.originalStrokeWidth;
        if (item.originalFill) {
            item.circle.style.fill = item.originalFill;
        }
    });
    highlightedCircles.length = 0;
    highlightedNodeNames.clear();
    exactMatchNodeNames.clear();

    setSearchTerm(searchTerm.trim());
    chartInstance.zoomReset();

    const matches: SearchMatch[] = [];
    function findMatches(node: CirclePackNode): void {
        const term = searchTerm.toLowerCase();
        const wordBoundaryRegex = new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        let isMatch = false;
        let isExactMatch = false;

        if (node.name && node.name.toLowerCase().includes(term)) {
            isMatch = true;
            if (wordBoundaryRegex.test(node.name)) {
                isExactMatch = true;
            }
        }

        if (!isMatch && node.purpose && node.purpose.toLowerCase().includes(term)) {
            isMatch = true;
            if (wordBoundaryRegex.test(node.purpose)) {
                isExactMatch = true;
            }
        }

        if (!isMatch && node.accountabilities && Array.isArray(node.accountabilities)) {
            isMatch = node.accountabilities.some(item => {
                if (item && item.toLowerCase().includes(term)) {
                    if (wordBoundaryRegex.test(item)) {
                        isExactMatch = true;
                    }
                    return true;
                }
                return false;
            });
        }

        if (!isMatch && node.responsibilities && Array.isArray(node.responsibilities)) {
            isMatch = node.responsibilities.some(item => {
                if (item && item.toLowerCase().includes(term)) {
                    if (wordBoundaryRegex.test(item)) {
                        isExactMatch = true;
                    }
                    return true;
                }
                return false;
            });
        }

        if (isMatch) {
            matches.push({ node, isExactMatch });
        }

        if (node.children) {
            node.children.forEach(findMatches);
        }
    }

    const currentData = chartInstance.data();
    if (currentData) {
        findMatches(currentData as any);
    }

    if (matches.length === 0) {
        showErrorModal('No Results', `No nodes found matching "${searchTerm}"`);
        return;
    }

    const chartElement = document.getElementById('chart');
    matches.forEach(({ node, isExactMatch }) => {
        highlightedNodeNames.add(node.name);
        if (isExactMatch) {
            exactMatchNodeNames.add(node.name);
        }
        const circles = chartElement.querySelectorAll('circle');
        circles.forEach(circle => {
            const circleData = circle.__data__;
            if (circleData && circleData.data && circleData.data.name === node.name) {
                const originalStroke = circle.style.stroke || circle.getAttribute('stroke');
                const originalStrokeWidth = circle.style.strokeWidth || circle.getAttribute('stroke-width') || '2';
                const originalFill = circle.style.fill || circle.getAttribute('fill');

                circle.classList.add('circle-highlighted-search');
                circle.style.strokeWidth = originalStrokeWidth;
                if (isExactMatch) {
                    circle.classList.add('circle-highlighted-exact');
                }

                highlightedCircles.push({
                    circle: circle,
                    originalStroke: originalStroke,
                    originalStrokeWidth: originalStrokeWidth,
                    originalFill: originalFill
                });
            }
        });
    });

    const clearBtn = document.getElementById('clear-search-btn');
    if (clearBtn) {
        clearBtn.classList.remove('hidden');
    }
}

export function clearHighlights(): void {
    highlightedCircles.forEach(item => {
        item.circle.classList.remove('circle-highlighted-search', 'circle-highlighted-exact');
        item.circle.style.stroke = item.originalStroke;
        item.circle.style.strokeWidth = item.originalStrokeWidth;
        if (item.originalFill) {
            item.circle.style.fill = item.originalFill;
        }
    });
    highlightedCircles.length = 0;
    highlightedNodeNames.clear();
    exactMatchNodeNames.clear();
    setSearchTerm('');

    const clearBtn = document.getElementById('clear-search-btn');
    if (clearBtn) {
        clearBtn.classList.add('hidden');
    }

    const searchInput = document.getElementById('search-input') as HTMLInputElement;
    if (searchInput) {
        searchInput.value = '';
    }

    const panelContent = document.querySelector('.role-panel__content') as HTMLElement;
    if (panelContent && panelContent.dataset.currentNode) {
        const currentData = chartInstance?.data();
        if (currentData) {
            const findNode = (node, name) => {
                if (node.name === name) return node;
                if (node.children) {
                    for (let child of node.children) {
                        const found = findNode(child, name);
                        if (found) return found;
                    }
                }
                return null;
            };
            const node = findNode(currentData, panelContent.dataset.currentNode);
            if (node) {
                displayNodeData(node);
            }
        }
    }
}

export function restoreHighlights(): void {
    if (highlightedNodeNames.size === 0 && !showContentNodesActive) return;

    const chartElement = document.getElementById('chart');
    const circles = chartElement.querySelectorAll('circle');

    highlightedCircles.length = 0;

    circles.forEach(circle => {
        const circleData = circle.__data__;
        if (circleData && circleData.data && highlightedNodeNames.has(circleData.data.name)) {
            const currentStroke = circle.getAttribute('stroke') || '#000000';
            const currentStrokeWidth = circle.getAttribute('stroke-width') || '2';
            const currentFill = circle.getAttribute('fill');

            circle.classList.add('circle-highlighted-search');
            circle.style.strokeWidth = currentStrokeWidth;
            if (exactMatchNodeNames.has(circleData.data.name)) {
                circle.classList.add('circle-highlighted-exact');
            }

            highlightedCircles.push({
                circle: circle,
                originalStroke: currentStroke,
                originalStrokeWidth: currentStrokeWidth,
                originalFill: currentFill
            });
        }
    });

    if (showContentNodesActive) {
        highlightContentNodes(true);
    }
}

export function setupSearch(): void {
    const searchInput = document.getElementById('search-input') as HTMLInputElement;
    if (searchInput) {
        searchInput.placeholder = t('search.placeholder');
        searchInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                searchAndHighlight(searchInput.value);
            }
        });
    }

    const clearBtn = document.getElementById('clear-search-btn');
    if (clearBtn) {
        clearBtn.addEventListener('click', clearHighlights);
    }
}
